/**
 * Project Service
 *
 * API client for project CRUD operations.
 */
import { api, getAuthHeaders } from './api';
import type { Project, FileNode } from '@/types';

export interface ProjectCreate {
  name: string;
  description?: string;
  template?: string;
}

export interface ProjectUpdate {
  name?: string;
  description?: string;
}

export interface ProjectListResponse {
  projects: Project[];
  total: number;
  limit: number;
  offset: number;
}

export interface ProjectFilesResponse {
  files: FileNode;
}

export const projectService = {
  /**
   * List all projects with pagination.
   */
  async list(limit = 20, offset = 0): Promise<ProjectListResponse> {
    return api.get<ProjectListResponse>('/projects', {
      params: { limit, offset },
    });
  },

  /**
   * Create a new project.
   */
  async create(data: ProjectCreate): Promise<Project> {
    return api.post<Project>('/projects', data);
  },

  /**
   * Get a project by ID.
   */
  async get(projectId: string): Promise<Project> {
    return api.get<Project>(`/projects/${projectId}`);
  },

  /**
   * Get the file tree for a project.
   */
  async getFiles(projectId: string): Promise<ProjectFilesResponse> {
    return api.get<ProjectFilesResponse>(`/projects/${projectId}/files`);
  },

  /**
   * Update a project.
   */
  async update(projectId: string, data: ProjectUpdate): Promise<Project> {
    return api.put<Project>(`/projects/${projectId}`, data);
  },

  /**
   * Delete a project.
   */
  async delete(projectId: string): Promise<void> {
    return api.delete(`/projects/${projectId}`);
  },

  /**
   * Read a file's content. GET /api/files/{id}/read?path=... (the same endpoint
   * the editor + agent file tools use). `path` is project-root-relative.
   */
  async readFile(projectId: string, path: string): Promise<{ path: string; content: string; language?: string }> {
    const rel = path.replace(/^\/+/, '');
    return api.get<{ path: string; content: string; language?: string }>(
      `/files/${projectId}/read`,
      { params: { path: rel } },
    );
  },

  /**
   * Update a file's content.
   */
  async updateFile(projectId: string, filePath: string, content: string): Promise<{ status: string; path: string }> {
    return api.put(`/projects/${projectId}/files${filePath}`, { content });
  },

  /**
   * Create a new file.
   */
  async createFile(projectId: string, filePath: string, content = ''): Promise<{ status: string; path: string }> {
    return api.put(`/projects/${projectId}/files${filePath}`, { content });
  },

  /**
   * Delete a file.
   */
  async deleteFile(projectId: string, filePath: string): Promise<void> {
    return api.delete(`/projects/${projectId}/files${filePath}`);
  },

  /**
   * Download a project file's raw bytes to the user's machine (any file type).
   * GET /api/files/{id}/download?path=... returns the bytes with a
   * Content-Disposition: attachment header. This exists because `readFile`
   * utf-8-decodes and can't retrieve a binary (a placed zip, a rendered
   * png/mp4). Uses a Bearer-attached fetch → blob → object URL → a temporary
   * `<a download>` click (the same authed-blob pattern as the render module's
   * fetchMediaObjectUrl). A bare string-literal `fetch('/api/...')` is banned by
   * the no-restricted-syntax lint rule; the template literal here is the
   * sanctioned form and still carries the Authorization header. `path` is
   * project-root-relative.
   */
  async downloadFile(projectId: string, path: string): Promise<void> {
    const rel = path.replace(/^\/+/, '');
    const res = await fetch(
      `/api/files/${projectId}/download?path=${encodeURIComponent(rel)}`,
      { headers: getAuthHeaders() },
    );
    if (!res.ok) throw new Error(`download ${rel} → ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = rel.split('/').pop() || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      URL.revokeObjectURL(url);
    }
  },
};

export default projectService;
